/**
 * Where the Eevo docs live: the public page (`/docs` on Eevo's hosts, `views/arvo-docs/`) and the
 * raw Markdown the API serves for agents (`/api/docs/…`, `upserve/modules/arvo/docs-handlers.ts`).
 * Kept apart from the content so a page can link to another without importing it.
 */
import { EEVO_OPENAPI_PATH, eevoApiPath } from '../api-paths';
import { ARVO_API_BASE_URL } from '../code-snippets';

/** The four kinds of page, in the order the docs list them (the Divio documentation system). */
export const ARVO_DOC_CATEGORIES = ['tutorials', 'how-to', 'reference', 'explanation'] as const;
export type ArvoDocCategory = (typeof ARVO_DOC_CATEGORIES)[number];

export const ARVO_DOC_CATEGORY_INFO: Record<ArvoDocCategory, { title: string; summary: string }> = {
  tutorials: {
    title: 'Tutorials',
    summary: 'Start here. Short lessons that take you from connecting your agent to a working decision, step by step.',
  },
  'how-to': {
    title: 'How-to guides',
    summary: 'Recipes for a task you already have in mind: choosing a mode, reporting chosen decisions, revising.',
  },
  reference: {
    title: 'Reference',
    summary: 'Authentication, the endpoints, MCP tools, models, prices, limits and errors, to look things up.',
  },
  explanation: {
    title: 'Explanation',
    summary: 'How Eevo decides and why it answers the way it does, for when you want to understand it.',
  },
};

export const ARVO_DOCS_UI_PATH = '/docs';
/**
 * Where the docs were before Eevo had a host of its own. Links to it (the marketing site,
 * bookmarks) land on the same page under `ARVO_DOCS_UI_PATH`.
 */
export const LEGACY_ARVO_DOCS_UI_PATH = '/docs/arvo';
/** Under upserve's `/api` prefix; the same files also answer under `/api/arvo/docs`, their earlier path. */
export const ARVO_DOCS_API_PATH = eevoApiPath('/docs');

/** A page of the docs, relative to the site (`/docs/tutorials/agent`). */
export function arvoDocsPath(category?: ArvoDocCategory, slug?: string, anchor?: string): string {
  const path = [ARVO_DOCS_UI_PATH, category, slug].filter(Boolean).join('/');
  return anchor ? `${path}#${anchor}` : path;
}

/** Pages that are gone, each with the page that took its place, so bookmarked links keep landing. */
const REPLACED_DOC_PAGES: Record<string, string> = {
  [arvoDocsPath('reference', 'skills')]: arvoDocsPath('reference', 'mcp'),
};

/** Where a removed page's address now points; null for every other path. */
export function replacedDocsPath(pathname: string): string | null {
  return REPLACED_DOC_PAGES[pathname] ?? null;
}

/** A path under `LEGACY_ARVO_DOCS_UI_PATH` as the same path under `ARVO_DOCS_UI_PATH`; null for any other path. */
export function movedFromLegacyDocsPath(pathname: string): string | null {
  if (pathname !== LEGACY_ARVO_DOCS_UI_PATH && !pathname.startsWith(`${LEGACY_ARVO_DOCS_UI_PATH}/`)) return null;
  return `${ARVO_DOCS_UI_PATH}${pathname.slice(LEGACY_ARVO_DOCS_UI_PATH.length)}`;
}

/** The same page as Markdown, served by the API for agents. */
export function arvoDocsMarkdownUrl(category: ArvoDocCategory, slug: string): string {
  return `${ARVO_API_BASE_URL}${ARVO_DOCS_API_PATH}/${category}/${slug}.md`;
}

export const ARVO_LLMS_TXT_URL = `${ARVO_API_BASE_URL}${ARVO_DOCS_API_PATH}/llms.txt`;
export const ARVO_OPENAPI_URL = `${ARVO_API_BASE_URL}${EEVO_OPENAPI_PATH}`;

/**
 * A Markdown link to a docs page, absolute so it works in the `.md` copies agents read as well as
 * on the docs page.
 */
export function docLink(label: string, category: ArvoDocCategory, slug: string, anchor?: string): string {
  return `[${label}](${ARVO_API_BASE_URL}${arvoDocsPath(category, slug, anchor)})`;
}
